import { useEffect, useState } from 'react';
import Page from '../../../components/Page';
import HeaderBreadcrumbs from '../../../components/HeaderBreadcrumbs';
import {
  Box,
  Button,
  Card,
  Container,
  IconButton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useParams } from 'react-router-dom';
import { decryptString } from '../../../utils/url';
import { useAppDispatch, useAppSelector } from '../../../store';
import Loader from '../../../components/Loader';
import Label from '../../../components/Label';
import Iconify from '../../../components/Iconify';
import { getPartner, patchPartner } from '../../../store/partner/api';
import { Partner } from '../../../store/partner/api/interface';
import {
  reset as resetTokens,
  setCurrentPage,
  setLimit,
  setSkip,
  setStatus,
} from '../../../store/partnerApiKey/partnerApiKeySlice';
import {
  createPartnerApiKey,
  findPartnerApiKey,
  patchPartnerApiKey,
  revokePartnerApiKey,
} from '../../../store/partnerApiKey/api';
import Scrollbar from '../../../components/Scrollbar';
import useToggle from '../../../hooks/useToggle';
import { useFormik } from 'formik';
import TokenFormModal from './TokenFormModal';
import SecretOnceDialog from './SecretOnceDialog';
import KeyViewDialog from './KeyViewDialog';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';

const scopeSummary = (
  scopes: string[]
): { label: string; detail: string }[] => {
  const groups = new Map<string, string[]>();
  scopes.forEach((scope) => {
    const [resource, action] = String(scope).split(':');
    const key = resource || scope;
    const actions = groups.get(key) || [];
    if (action) {
      actions.push(action);
    }
    groups.set(key, actions);
  });
  return Array.from(groups.entries()).map(([label, actions]) => ({
    label,
    detail: actions.length ? actions.join(', ') : label,
  }));
};

const formatWhen = (value?: string | null): string => {
  if (!value) {
    return '-';
  }
  return new Date(value).toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const shortKeyId = (key: string): string => {
  const prefix = key.match(/^(adori_pk_(?:test|live)_)/);
  if (prefix) {
    return `${prefix[1]}....`;
  }
  return key.length > 16 ? `${key.slice(0, 14)}....` : key;
};

type Params = {
  id: string;
};

const Index = () => {
  const dispatch = useAppDispatch();
  const params = useParams<Params>();
  const partnerId = decryptString(params.id ?? '');
  const [partner, setPartner] = useState<Partner>();
  const { data, totalRecords, limit, currentPage, status, skip, isLoading } =
    useAppSelector((state) => state.partnerApiKey);
  const { toggle, onOpen, onClose } = useToggle();
  const {
    toggle: secretToggle,
    onOpen: onOpenSecret,
    onClose: onCloseSecret,
  } = useToggle();
  const [createdSecret, setCreatedSecret] = useState({
    secret: '',
    publicKey: '',
  });
  const {
    toggle: viewKeyToggle,
    onOpen: onOpenViewKey,
    onClose: onCloseViewKey,
  } = useToggle();
  const [viewPublicKey, setViewPublicKey] = useState('');

  useEffect(() => {
    if (!partnerId) {
      return;
    }
    dispatch(getPartner({ id: partnerId }))
      .unwrap()
      .then((row) => setPartner(row))
      .catch(() => {
        setPartner(undefined);
      });
  }, [dispatch, partnerId]);

  useEffect(() => {
    dispatch(resetTokens());
  }, [partnerId, dispatch]);

  useEffect(() => {
    if (status === 'idle' && partnerId) {
      dispatch(
        findPartnerApiKey({
          partnerId,
          limit,
          skip,
        })
      );
    }
  }, [dispatch, limit, skip, status, partnerId]);

  const { handleSubmit, values, resetForm, isSubmitting, setFieldValue } =
    useFormik({
      initialValues: {
        env: 'test',
        preset: 'full',
      },
      onSubmit: async (formValues) => {
        try {
          const created = await dispatch(
            createPartnerApiKey({
              partnerId,
              env: formValues.env,
              preset: formValues.preset,
            })
          ).unwrap();
          setCreatedSecret({
            secret: created.secret || '',
            publicKey: created.publicKey || '',
          });
          onClose();
          resetForm();
          onOpenSecret();
          dispatch(setStatus({ status: 'idle' }));
        } catch (error: any) {}
      },
    });

  const handleTogglePartner = async (): Promise<void> => {
    if (!partner) {
      return;
    }
    const updated = await dispatch(
      patchPartner({ id: partner.id, isActive: !partner.isActive })
    ).unwrap();
    setPartner(updated);
  };

  const handleToggleToken = async (id: string, isActive: boolean) => {
    await dispatch(patchPartnerApiKey({ id, isActive: !isActive })).unwrap();
    dispatch(setStatus({ status: 'idle' }));
  };

  const handleRevoke = async (id: string) => {
    await dispatch(revokePartnerApiKey({ id })).unwrap();
    dispatch(setStatus({ status: 'idle' }));
  };

  const handlePageChange = (
    _event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number
  ): void => {
    dispatch(setSkip({ skip: limit * newPage }));
    dispatch(setCurrentPage({ currentPage: newPage }));
    dispatch(setStatus({ status: 'idle' }));
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    dispatch(setLimit({ limit: +event.target.value }));
  };

  const showActiveColumn = data.some((token) => token.status !== 'revoked');
  const columnCount = showActiveColumn ? 7 : 6;

  return (
    <Page title={'Partner: View'}>
      <Container maxWidth={'xl'}>
        <HeaderBreadcrumbs
          heading={partner?.name || 'Partner'}
          links={[
            { name: 'Dashboard', href: '/' },
            { name: 'Partners', href: '/partners' },
          ]}
          action={
            <Button
              variant='contained'
              startIcon={<Iconify icon={'eva:plus-fill'} />}
              onClick={onOpen}
            >
              New Token
            </Button>
          }
        />

        <Card sx={{ px: 3, py: 2.5, mb: 3 }}>
          {!partner ? (
            <Loader />
          ) : (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant='h6'
                  noWrap
                >
                  {partner.name}
                </Typography>
                <Typography
                  variant='body2'
                  color='text.secondary'
                >
                  {totalRecords} token{totalRecords === 1 ? '' : 's'}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  flexWrap: 'nowrap',
                  gap: 0.5,
                }}
              >
                <Typography
                  variant='body2'
                  sx={{
                    fontWeight: 700,
                    whiteSpace: 'nowrap',
                    color: partner.isActive ? 'success.dark' : 'error.main',
                  }}
                >
                  {partner.isActive ? 'Active' : 'Inactive'}
                </Typography>
                <Switch
                  checked={partner.isActive}
                  onChange={handleTogglePartner}
                  inputProps={{ 'aria-label': 'toggle partner' }}
                  sx={(theme) => ({
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: theme.palette.success.main,
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: theme.palette.success.main,
                    },
                    '& .MuiSwitch-switchBase:not(.Mui-checked)': {
                      color: theme.palette.error.main,
                    },
                    '& .MuiSwitch-switchBase:not(.Mui-checked) + .MuiSwitch-track':
                      {
                        backgroundColor: theme.palette.error.main,
                        opacity: 0.6,
                      },
                  })}
                />
              </Box>
            </Box>
          )}
        </Card>

        <Card>
          <Box sx={{ p: 2 }}>
            <Typography variant='subtitle1'>Tokens</Typography>
          </Box>
          <Scrollbar>
            <TableContainer sx={{ position: 'relative' }}>
              <Table
                size={'medium'}
                sx={{ tableLayout: 'fixed', width: '100%' }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell
                      align='center'
                      sx={{ width: '16%' }}
                    >
                      Key id
                    </TableCell>
                    <TableCell
                      align='center'
                      sx={{ width: '10%' }}
                    >
                      Env
                    </TableCell>
                    <TableCell
                      align='center'
                      sx={{ width: '18%' }}
                    >
                      Scopes
                    </TableCell>
                    {showActiveColumn && (
                      <TableCell
                        align='center'
                        sx={{ width: 88, whiteSpace: 'nowrap' }}
                      >
                        Active
                      </TableCell>
                    )}
                    <TableCell
                      align='center'
                      sx={{ whiteSpace: 'nowrap', width: '20%' }}
                    >
                      Created
                    </TableCell>
                    <TableCell
                      align='center'
                      sx={{ whiteSpace: 'nowrap', width: '16%' }}
                    >
                      Revoked
                    </TableCell>
                    <TableCell
                      align='center'
                      sx={{ whiteSpace: 'nowrap', minWidth: 112 }}
                    >
                      Action
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell
                        colSpan={columnCount}
                        align='center'
                      >
                        <Loader />
                      </TableCell>
                    </TableRow>
                  ) : data?.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={columnCount}
                        align='center'
                      >
                        No record found
                      </TableCell>
                    </TableRow>
                  ) : (
                    data.map((token) => (
                      <TableRow key={token.id}>
                        <TableCell align='center'>
                          {shortKeyId(token.publicKey)}
                        </TableCell>
                        <TableCell align='center'>
                          <Label
                            variant='ghost'
                            color={token.env === 'live' ? 'warning' : 'info'}
                          >
                            {token.env}
                          </Label>
                        </TableCell>
                        <TableCell
                          align='center'
                          sx={{ overflow: 'hidden' }}
                        >
                          {(() => {
                            const groups = scopeSummary(token.scopes || []);
                            const summary = groups.map((group) => group.label).join(', ');
                            const detail = groups
                              .map((group) => `${group.label}: ${group.detail}`)
                              .join('\n');
                            return (
                              <Tooltip
                                title={
                                  <Box
                                    component='span'
                                    sx={{ whiteSpace: 'pre-line' }}
                                  >
                                    {detail || '-'}
                                  </Box>
                                }
                              >
                                <Typography
                                  variant='body2'
                                  noWrap
                                  sx={{ textTransform: 'capitalize' }}
                                >
                                  {summary || '-'}
                                </Typography>
                              </Tooltip>
                            );
                          })()}
                        </TableCell>
                        {showActiveColumn && (
                          <TableCell
                            align='center'
                            sx={{ whiteSpace: 'nowrap' }}
                          >
                            {token.status !== 'revoked' && (
                              <Switch
                                checked={token.status === 'active'}
                                onChange={() =>
                                  handleToggleToken(
                                    token.id,
                                    token.status === 'active'
                                  )
                                }
                                inputProps={{ 'aria-label': 'toggle token' }}
                              />
                            )}
                          </TableCell>
                        )}
                        <TableCell
                          align='center'
                          sx={{ whiteSpace: 'nowrap', overflow: 'hidden' }}
                        >
                          {formatWhen(token.createdAt)}
                        </TableCell>
                        <TableCell
                          align='center'
                          sx={{ whiteSpace: 'nowrap', overflow: 'hidden' }}
                        >
                          {formatWhen(token.revokedAt)}
                        </TableCell>
                        <TableCell
                          align='center'
                          sx={{ whiteSpace: 'nowrap' }}
                        >
                          <Box
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexWrap: 'nowrap',
                            }}
                          >
                            <IconButton
                              aria-label='view'
                              onClick={() => {
                                setViewPublicKey(token.publicKey);
                                onOpenViewKey();
                              }}
                            >
                              <RemoveRedEyeIcon />
                            </IconButton>
                            {token.status !== 'revoked' && (
                              <IconButton
                                aria-label='revoke'
                                onClick={() => handleRevoke(token.id)}
                              >
                                <Iconify icon={'eva:trash-2-outline'} />
                              </IconButton>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Scrollbar>
          <Box sx={{ position: 'relative' }}>
            <TablePagination
              rowsPerPageOptions={[25, 50, 100]}
              component='div'
              count={totalRecords}
              rowsPerPage={limit}
              page={Math.min(Math.round(totalRecords / limit), currentPage)}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
            />
          </Box>
        </Card>

        <TokenFormModal
          handleSubmit={handleSubmit}
          isSubmitting={isSubmitting}
          onClose={onClose}
          resetForm={resetForm}
          setFieldValue={setFieldValue}
          toggle={toggle}
          values={values}
        />
        <SecretOnceDialog
          toggle={secretToggle}
          secret={createdSecret.secret}
          publicKey={createdSecret.publicKey}
          onClose={onCloseSecret}
        />
        <KeyViewDialog
          toggle={viewKeyToggle}
          publicKey={viewPublicKey}
          onClose={onCloseViewKey}
        />
      </Container>
    </Page>
  );
};

export default Index;
